// ./RegistrationFields/CompanyManagementType/CompanyManagementType.jsx

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  FormAutosuggest,
  FormAutosuggestOption,
  FormControlFeedback,
} from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { clearRegistrationBackendError } from '../../data/actions';
import messages from '../../messages';

const AGE_VALUE_KEY = 'value';
const AGE_LABEL_KEY = 'label';

const DEFAULT_AGE_RANGE_LIST = [
  { value: '18-30', label: '18-30' },
  { value: '30-50', label: '30-50' },
  { value: 'Over 50', label: 'Over 50' }
];

const AgeField = (props) => {
  const {
    name,
    value,
    handleChange,
    handleErrorChange,
    errorMessage,
    floatingLabel,
    ageRangeList,
  } = props;

  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const ageRanges = ageRangeList || DEFAULT_AGE_RANGE_LIST;
  const [inputText, setInputText] = useState('');

  const selected = ageRanges.find(
    item => item[AGE_VALUE_KEY] === value,
  ) || { value: '', label: '' };

  useEffect(() => {
    if (value) {
      setInputText(selected[AGE_LABEL_KEY] || '');
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const fieldValue = {
    userProvidedText: inputText,
    selectionValue: selected[AGE_LABEL_KEY] || '',
    selectionId: selected[AGE_VALUE_KEY] || '',
  };

  const requiredError = formatMessage(
    messages['empty.age.field.error'],
  );

  const runValidation = (val) => {
      const trimmed = (val || '').trim();
    if (!trimmed) {
      handleErrorChange(name, requiredError);
    } else {
      handleErrorChange(name, '');
    }
  };

  const handleOnFocus = () => {
    dispatch(clearRegistrationBackendError(name));
    handleErrorChange(name, '');
  };

  const handleOnBlur = () => {
    runValidation(value);
  };

  const handleOnChangeAutosuggest = (autosuggestValue) => {
    const code = autosuggestValue.selectionId || '';
    setInputText(autosuggestValue.userProvidedText || autosuggestValue.selectionValue || '');

    handleChange({
      target: {
        name,
        value: code,
      },
    });

    // Show "No results found" while typing; enforce required validation on blur/submit.
    handleErrorChange(name, '');
  };

  const getOptions = () => ageRanges.map(option => (
    <FormAutosuggestOption
      key={option[AGE_VALUE_KEY]}
      id={option[AGE_VALUE_KEY]}
    >
      {option[AGE_LABEL_KEY]}
    </FormAutosuggestOption>
  ));
  const hasMatchingOptions = !inputText.trim()
    || ageRanges.some(option => option[AGE_LABEL_KEY].toLowerCase().includes(inputText.toLowerCase()));

  return (
    <div className="mb-4">
      <FormAutosuggest
        floatingLabel={
          floatingLabel
          || formatMessage(messages['registration.age.label'])
        }
        aria-label="age range"
        name={name}
        value={fieldValue || {}}
        className={classNames({ 'form-field-error': errorMessage })}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onChange={handleOnChangeAutosuggest}
      >
        {getOptions()}
      </FormAutosuggest>
      {errorMessage && (
        <FormControlFeedback
          key="error"
          className="form-text-size"
          hasIcon={false}
          feedback-for={name}
          type="invalid"
        >
          {errorMessage}
        </FormControlFeedback>
      )}
      {!errorMessage && !hasMatchingOptions && (
        <div className="autosuggest-no-results">{formatMessage(messages['registration.no.results.found'])}</div>
      )}
    </div>
  );
};

AgeField.propTypes = {
  name: PropTypes.string.isRequired,          // e.g. "ageRange"
  value: PropTypes.string.isRequired,         // e.g. "18-30"
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  floatingLabel: PropTypes.string,
  ageRangeList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
};

AgeField.defaultProps = {
  errorMessage: '',
  floatingLabel: '',
  ageRangeList: null,
};

export default AgeField;
